<script>
import { mapGetters } from "vuex";
import { format } from "date-fns";
import indicationService from "@/services/indication";
import patientReportService from "@/services/patient-report";
import IndicationCard from "@/components/appointment/IndicationCard.vue";
import ModalPdfViewer from "@/components/appointment/ModalPdfViewer.vue";
import Spinner from "@/components/ui/Spinner";
import { capitalizeWords, getDoctorLabel } from "@/utils/names";

// Junta en una sola lista los dos tipos de informe que llegan por HL7:
// - texto: son indicaciones imagesResults (también se ven en la HC).
// - PDF: están en su propia colección y solo se ven acá, con Ver y Descargar.
export default {
  name: "ModalImageReports",

  components: { IndicationCard, ModalPdfViewer, Spinner },

  props: {
    visible: { type: Boolean, required: true },
    patientId: { type: String, default: null },
    patientName: { type: String, default: "" },
  },

  data() {
    return {
      isLoading: false,
      indications: [],
      pdfReports: [],
      loadingReportId: null,
      pdfViewer: { visible: false, url: null, title: "" },
    };
  },

  computed: {
    ...mapGetters(["isDoctor", "isAdmin"]),

    // Ver y descargar el PDF queda para médico y admin, igual que el botón
    // de imprimir que había antes para estos informes.
    canOpenReportPdf() {
      return this.isDoctor || this.isAdmin;
    },

    formattedPatientName() {
      return capitalizeWords(this.patientName);
    },

    hasReports() {
      return this.indications.length > 0 || this.pdfReports.length > 0;
    },

    groupedByDate() {
      const items = [
        ...this.indications.map((indication) => ({
          kind: "text",
          id: indication._id,
          date: indication.createdAt,
          indication,
        })),
        ...this.pdfReports.map((report) => ({
          kind: "pdf",
          id: report._id,
          date: report.observationDate,
          report,
        })),
      ].sort((a, b) => new Date(b.date || 0) - new Date(a.date || 0));

      const groups = {};
      for (const item of items) {
        const key = item.date
          ? format(new Date(item.date), "yyyy-MM-dd")
          : "sin-fecha";
        if (!groups[key]) {
          groups[key] = { key, date: item.date || null, items: [] };
        }
        groups[key].items.push(item);
      }
      return Object.values(groups);
    },
  },

  watch: {
    visible(val) {
      if (val && this.patientId) {
        this.loadReports();
      } else {
        this.indications = [];
        this.pdfReports = [];
      }
    },
  },

  methods: {
    async loadReports() {
      this.isLoading = true;
      const [indications, pdfReports] = await Promise.all([
        indicationService
          .get(this.patientId, [{ type: "imagesResults" }])
          .catch(() => null),
        patientReportService.list(this.patientId).catch(() => null),
      ]);
      if (!indications || !pdfReports) {
        this.$message.error("Error al cargar los informes");
      }
      this.indications = indications || [];
      this.pdfReports = pdfReports || [];
      this.isLoading = false;
    },

    getDoctorLabel,

    async onViewPdf(report) {
      this.loadingReportId = report._id;
      try {
        const url = await patientReportService.getViewUrl(report._id);
        this.pdfViewer = { visible: true, url, title: report.title };
      } catch {
        this.$message.error("No se pudo abrir el informe");
      } finally {
        this.loadingReportId = null;
      }
    },

    async onDownloadPdf(report) {
      this.loadingReportId = report._id;
      try {
        // La URL viene con Content-Disposition attachment: el navegador
        // descarga el archivo sin salir de la app.
        const url = await patientReportService.getDownloadUrl(report._id);
        const link = document.createElement("a");
        link.href = url;
        link.rel = "noopener";
        document.body.appendChild(link);
        link.click();
        link.remove();
      } catch {
        this.$message.error("No se pudo descargar el informe");
      } finally {
        this.loadingReportId = null;
      }
    },

    close() {
      this.pdfViewer = { visible: false, url: null, title: "" };
      this.$emit("update:visible", false);
    },
  },
};
</script>

<template lang="pug">
  el-dialog.modal-image-reports(
    :visible="visible"
    :before-close="close"
    title="Informes"
    width="640px"
  )
    p.modal-patient-name(v-if="patientName") {{ formattedPatientName }}
    spinner(v-if="isLoading")
    p.modal-empty(v-else-if="!hasReports") Sin informes registrados.
    div.reports-list(v-else)
      div.report-group(v-for="group in groupedByDate" :key="group.key")
        p.report-date {{ group.date | formatDate }}
        template(v-for="item in group.items")
          indication-card(
            v-if="item.kind === 'text'"
            :key="item.id"
            :indication="item.indication"
            :can-edit="false"
            :is-type-allowed="false"
            :can-resend="false"
          )
          //- Mismo diseño que la tarjeta de un informe de texto (IndicationCard):
          //- la fecha va solo en el encabezado del día, igual que en el texto.
          .pdf-report-card(v-else :key="item.id")
            .pdf-report-header
              micon(name="assignment")
              span Resultados de imágenes
              span.pdf-badge PDF
              span.doctor-info(v-if="getDoctorLabel(item.report.doctor)") {{ getDoctorLabel(item.report.doctor) }}
            .pdf-report-body
              p
                strong.label-report Estudio:
                | {{ item.report.title }}
            .pdf-report-actions(v-if="canOpenReportPdf")
              button.button-glass-new.small(
                :disabled="loadingReportId === item.id"
                @click="onViewPdf(item.report)"
              ) Ver
              button.button-glass-new.small(
                :disabled="loadingReportId === item.id"
                @click="onDownloadPdf(item.report)"
              ) Descargar

    modal-pdf-viewer(
      :visible.sync="pdfViewer.visible"
      :url="pdfViewer.url"
      :title="pdfViewer.title"
    )
</template>

<style lang="scss" scoped>
.modal-patient-name {
  font-weight: 600;
  font-size: 16px;
  margin-bottom: 16px;
  color: #405186;
}
.modal-empty {
  color: #888;
  text-align: center;
  padding: 24px 0;
}
.reports-list {
  max-height: 60vh;
  overflow-y: auto;
}
.report-group {
  margin-bottom: 16px;
}
.report-date {
  font-size: 13px;
  font-weight: 600;
  color: #666;
  margin-bottom: 8px;
  padding-bottom: 4px;
  border-bottom: 1px solid #eee;
}
.pdf-report-card {
  margin-bottom: 12px;
  border: solid 2px #d5dbf3;
  border-radius: 10px;
  background-color: #fff;
}
.pdf-report-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 12px;
  font-weight: 600;
  font-size: 15px;
  border-bottom: 2px solid #d5dbf3;
}
.pdf-badge {
  background-color: #d5dbf3;
  color: #1e3270;
  border-radius: 10px;
  font-size: 10px;
  padding: 2px 8px;
}
.doctor-info {
  margin-left: auto;
  font-size: 11px;
  font-weight: 400;
  color: #6b7aad;
}
.pdf-report-body {
  padding: 7px 10px;
  font-size: 14px;
  p {
    margin: 0;
  }
}
.label-report {
  font-weight: 600;
  margin-right: 4px;
}
.pdf-report-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 0 12px 8px;
}
.small {
  font-size: 11px;
  padding: 4px 8px;
  height: auto;
  min-height: 24px;
}
</style>
